<script setup lang="ts">
import { useHead } from '@unhead/vue';
import AppImage from '@/components/shared/AppImage.vue';
import Banner from '@/components/shared/Banner.vue';

useHead({
  title: 'Student Assurance Services',
  meta: [
    {
      name: 'description',
      content:
        'Student Assurance Services provides affordable accident insurance for K-12 students, college students, and special risk programs.',
    },
  ],
});
</script>

<template>
  <Banner image="home" alt="Students walking on campus">
    <div class="flex flex-col gap-15 md:gap-30">
      <h1>Specialists in<br />student accident<br />and health insurance</h1>
      <p class="text-15 xl:text-18 leading-[1.6]">
        We offer insurance plan solutions to school<br />districts, private schools, universities,
        colleges<br />and specialty groups.
      </p>
      <RouterLink to="/aboutus/allproducts" class="btn-blue xl:mt-20"
        >View Products &amp; Services</RouterLink
      >
    </div>
  </Banner>
  <div class="container grid grid-cols-6 gap-30 py-56 text-white">
    <div class="card-teal relative col-span-full h-450 overflow-hidden lg:col-span-4 xl:h-500">
      <AppImage
        name="k12-student"
        alt="K-12 student"
        sizes="(min-width: 1000px) 33vw, 50vw"
        img-class="pointer-events-none absolute right-0 bottom-0 h-full w-1/2 object-contain object-bottom-right xl:w-[48%] 2xl:w-[45%]"
      />
      <div
        class="flex h-full flex-col justify-center gap-30 px-20 min-[500px]:*:max-w-200 sm:*:max-w-220 md:*:max-w-250 lg:px-30 lg:*:max-w-280 xl:px-50 xl:*:max-w-300 2xl:px-70 2xl:*:max-w-320"
      >
        <h2
          class="xl:text-39 md:text-29 text-24 xs:max-[500px]:-mt-50 leading-none font-bold min-[500px]:max-w-185!"
        >
          K12 Students &amp; Parents
        </h2>
        <p>
          As a service to the community, your school has contracted with us to offer or administer
          an accident insurance plan for your student.
        </p>
        <div class="flex gap-15 max-md:flex-col lg:mt-15 xl:mt-30">
          <RouterLink to="/k12" class="btn-outline-orange">Enroll</RouterLink>
          <RouterLink to="/k12#schoollookup" class="btn-outline-orange">Find My School</RouterLink>
        </div>
      </div>
    </div>
    <div
      class="card-pink col-span-full flex flex-col justify-center gap-30 p-20 md:col-span-3 lg:col-span-2 lg:px-30 xl:gap-50 xl:px-50"
    >
      <h2 class="xl:text-29 text-21 leading-none font-bold">K12 Administrators</h2>
      <p>
        As a service to our member schools, we are providing access to information about your
        student accident insurance program.
      </p>
      <div
        class="*:link-arrow *:before:border-orange *:hover:text-orange *:focus:text-orange flex flex-wrap gap-x-20 gap-y-5 font-medium uppercase *:w-fit md:flex-col"
      >
        <RouterLink to="/k12/admin/login">Access your school's account</RouterLink>
        <RouterLink to="/aboutus/allproducts">View our services</RouterLink>
      </div>
    </div>
    <div
      class="card-purple col-span-full flex flex-col justify-center gap-30 p-20 md:col-span-3 lg:col-span-2 lg:px-30 xl:gap-50 xl:px-50"
    >
      <h2 class="xl:text-29 text-21 leading-none font-bold">College Administrators</h2>
      <p>
        Our commitment is to provide you with high quality, cost-effective insurance plans to ensure
        the well-being.
      </p>
      <div
        class="*:link-arrow *:before:border-orange *:hover:text-orange *:focus:text-orange flex flex-wrap gap-x-20 gap-y-5 font-medium uppercase *:w-fit md:flex-col"
      >
        <RouterLink to="/college/admin/login">Access your school's account</RouterLink>
        <RouterLink to="/aboutus/allproducts">View our services</RouterLink>
      </div>
    </div>
    <div class="card-blue relative col-span-full flex h-420 overflow-hidden lg:col-span-4 xl:h-475">
      <AppImage
        name="college-student"
        alt="College student"
        sizes="(min-width: 1000px) 33vw, 50vw"
        img-class="pointer-events-none absolute right-0 bottom-0 h-full w-1/2 object-contain object-bottom-right xl:w-[48%] 2xl:w-[45%]"
      />
      <div
        class="flex h-full flex-col justify-center gap-30 px-20 min-[500px]:*:max-w-200 sm:*:max-w-220 md:*:max-w-250 lg:px-30 lg:*:max-w-280 xl:px-50 xl:*:max-w-300 2xl:px-70 2xl:*:max-w-320"
      >
        <h2 class="xl:text-39 md:text-29 text-24 xs:max-[500px]:-mt-50 leading-none font-bold">
          College Students
        </h2>
        <p>
          Access to student insurance is critical to your success and well-being of your students.
          Learn more about your school's insurance plan.
        </p>
        <div class="flex gap-15 max-md:flex-col lg:mt-15 xl:mt-30">
          <RouterLink to="/college" class="btn-outline-orange">Enroll</RouterLink>
          <RouterLink to="/college#schoollookup" class="btn-outline-orange"
            >Find My School</RouterLink
          >
        </div>
      </div>
    </div>
    <iframe
      class="col-span-full aspect-video w-full rounded-2xl shadow-lg"
      src="https://www.youtube-nocookie.com/embed/6yJJc4Ls1cY?si=4ULSHzUtX5S3dC8n"
      title="YouTube video player"
      frameborder="0"
      allow="
        accelerometer;
        autoplay;
        clipboard-write;
        encrypted-media;
        gyroscope;
        picture-in-picture;
        web-share;
      "
      referrerpolicy="strict-origin-when-cross-origin"
      allowfullscreen
    ></iframe>
  </div>
</template>
